import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal, ElementRef, viewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SupabaseService } from '../../services/supabase.service';
import { AuthService } from '../../services/auth.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule]
})
export class LoginComponent implements OnDestroy {
  private supabaseService = inject(SupabaseService);
  private authService = inject(AuthService);
  // FIX: Explicitly typed `router` to resolve a type inference issue where the compiler
  // treated it as `unknown`, preventing access to its methods.
  private router: Router = inject(Router);

  loading = signal(false);
  error = signal<string | null>(null);

  email = signal('');
  otp = signal('');
  otpSent = signal(false);
  cooldownSeconds = signal(0);
  private timerInterval: any;
  private readonly COOLDOWN_KEY = 'otp_cooldown_end';

  isLoggedInButNotReviewer = this.authService.isLoggedInButNotReviewer;
  
  otpInput = viewChild<ElementRef<HTMLInputElement>>('otpInput');

  constructor() {
    console.log('[LoginComponent] Initialized. Current isLoggedInButNotReviewer state:', this.isLoggedInButNotReviewer());
    this.checkInitialCooldown();
    
    effect(() => {
      const inputEl = this.otpInput();
      if (inputEl) {
        // Use a microtask to focus after the view is stable.
        setTimeout(() => inputEl.nativeElement.focus(), 0);
      }
    });
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  private checkInitialCooldown() {
    const cooldownEnd = localStorage.getItem(this.COOLDOWN_KEY);
    if (cooldownEnd) {
        const remainingTime = Math.ceil((parseInt(cooldownEnd, 10) - Date.now()) / 1000);
        if (remainingTime > 0) {
            this.startCooldown(remainingTime, false);
        } else {
            localStorage.removeItem(this.COOLDOWN_KEY);
        }
    }
  }

  private startCooldown(seconds: number, setStorage = true) {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.cooldownSeconds.set(seconds);
    
    if (setStorage) {
      const cooldownEndTime = Date.now() + seconds * 1000;
      localStorage.setItem(this.COOLDOWN_KEY, cooldownEndTime.toString());
    }

    this.timerInterval = setInterval(() => {
        this.cooldownSeconds.update(s => {
            if (s <= 1) {
                clearInterval(this.timerInterval);
                localStorage.removeItem(this.COOLDOWN_KEY);
                return 0;
            }
            return s - 1;
        });
    }, 1000);
  }

  async handleSendOtp() {
    if (this.cooldownSeconds() > 0) return;
    
    console.log('[LoginComponent] handleSendOtp called.');
    this.loading.set(true);
    this.error.set(null);

    const email = this.email().trim();

    if (!email.toLowerCase().endsWith('@cordic.com')) {
      this.error.set('Only cordic.com emails are permitted.');
      this.loading.set(false);
      return;
    }

    try {
      await this.supabaseService.signInWithEmailOtp(email);
      this.otpSent.set(true);
      this.startCooldown(60);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send login code.';
      
      // Check for Supabase rate-limiting error with explicit seconds
      const cooldownMatch = errorMessage.match(/after (\d+) seconds/i);
      
      if (cooldownMatch && cooldownMatch[1]) {
        const seconds = parseInt(cooldownMatch[1], 10);
        this.startCooldown(seconds);
        this.error.set(`You're trying too frequently. Please wait a moment.`);
      } else if (error.status === 429 || (errorMessage && errorMessage.toLowerCase().includes('invalid json response'))) {
         // Handle generic 429 rate limit errors or JSON parsing failures which are likely rate limits
        const defaultCooldown = 60; // A safe default
        this.startCooldown(defaultCooldown);
        this.error.set(`Rate limit exceeded. Please wait about a minute before trying again.`);
        console.warn(`[LoginComponent] Rate limit error (429 or JSON parse error) received without specific cooldown time. Applying default ${defaultCooldown}s cooldown.`);
      }
      else {
        this.error.set(errorMessage); // Show other errors
      }
      
      console.error('[LoginComponent] Error during signInWithEmailOtp:', error);
    } finally {
      this.loading.set(false);
    }
  }
  
  async handleOtpSubmit() {
    console.log('[LoginComponent] handleOtpSubmit called.');
    this.loading.set(true);
    this.error.set(null);
    try {
      await this.supabaseService.verifyEmailOtp(this.email().trim(), this.otp());
      // On success, the auth state change will trigger a redirect via the auth.service effect.
      // However, to make this more robust, we navigate directly from here.
      console.log('[LoginComponent] OTP verification successful. Navigating to applications...');
      this.router.navigate(['/applications']);
    } catch (error: any) {
      this.error.set(error.message || 'Failed to verify code. It may be incorrect or expired.');
      console.error('[LoginComponent] Error during verifyEmailOtp:', error);
      // Only turn off loading if there's an error, allowing the user to try again.
      this.loading.set(false);
    }
  }

  goBack() {
    this.otpSent.set(false);
    this.otp.set('');
    this.error.set(null);
  }

  async handleLogout() {
    console.log('[LoginComponent] handleLogout called. Attempting to sign out...');
    this.loading.set(true);
    try {
      await this.supabaseService.signOut();
      console.log('[LoginComponent] signOut call successful.');
    } catch(e) {
      console.error('[LoginComponent] Logout failed', e);
    } finally {
      this.loading.set(false);
    }
  }
}
